/**
 * The Scores chip row (Q277, Scores UX spec v1.0 section 3.2): a horizontal
 * scroller of filter chips, `All` first and then one chip per pinned or
 * favorite-team league, with the "+ Leagues" manage control pinned to the
 * right end OUTSIDE the scroller, so it never scrolls away.
 *
 * Selecting a chip filters in place through `?leagueId=`; it never opens a
 * new screen. The manage control opens the league menu.
 *
 * Chips are 36px pills (`chipHeight`): `surface` with a `border` outline, or
 * `primarySolid` with `onPrimary` text and a border of the same color when
 * selected, so the width never shifts. The fill animates. Every chip sits in
 * a real 44px button: the scroller is `overflow-x-auto`, whose computed
 * `overflow-y` is `auto`, so it would clip a hit-slop overlay (ISSUE-773). The
 * 4px above and below the visible pill is the button's own transparent box.
 *
 * The chips are a `tablist` named "Leagues" with `aria-selected`, and the
 * arrow keys move focus between them (roving tabindex: only the selected chip
 * is in the tab order).
 *
 * The selected chip is kept in view (ISSUE-949 R9): on first render and
 * whenever the selection or the chip list changes, the scroller moves by the
 * smallest horizontal scroll that shows the whole chip, and not at all when it
 * already shows. It writes only its own scrollLeft, never the page's.
 */
import { cn } from "@sportsmet/ui";
import {
  type KeyboardEvent,
  type ReactNode,
  useLayoutEffect,
  useRef,
} from "react";

import { PlusIcon } from "../../icons/glyphs";
import {
  OUTLINE_PILL_CLASS,
  OUTLINE_PILL_TARGET_CLASS,
} from "../standings/StandingsLinkButton";
import { scrollLeftToRevealChip } from "./leagueRowReveal";
import {
  LEAGUE_CHIPS_LABEL,
  LEAGUE_MENU_MANAGE_LABEL,
  LEAGUE_MENU_OPEN_LABEL,
  type typeLeagueSelectorRow,
} from "./leagueSelectorContract";

export type typeLeagueSelectorRowProps = {
  /** From buildLeagueSelectorRow in leagueSelectorRowModel.ts. */
  row: typeLeagueSelectorRow;
  /** Clears ?leagueId= and returns to the All feed. */
  onSelectAll: () => void;
  /** Sets ?leagueId= to this league. */
  onSelectLeague: (leagueId: string) => void;
  /** Sets ?sheet=leagues. Wired to the manage control only. */
  onOpenMenu: () => void;
};

/** The visible chip inside its 44px button. */
export function chipPillClassName(isSelected: boolean): string {
  return cn(
    "inline-flex h-chip-height min-w-touch-target items-center justify-center rounded-full border px-3 text-caption",
    "transition-colors duration-200 motion-reduce:transition-none",
    "group-focus-visible:outline-2 group-focus-visible:outline-offset-2 group-focus-visible:outline-focus-ring",
    isSelected
      ? "border-primary-solid bg-primary-solid text-on-primary"
      : "border-line bg-surface text-ink group-hover:bg-surface-sunken",
  );
}

const CHIP_BUTTON_CLASS =
  "group flex min-h-touch-target min-w-touch-target shrink-0 items-center justify-center focus-visible:outline-none";

function moveFocus(event: KeyboardEvent<HTMLDivElement>): void {
  if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") {
    if (event.key !== "Home" && event.key !== "End") return;
  }
  const tabs = [
    ...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'),
  ];
  if (tabs.length === 0) return;
  const current = tabs.indexOf(document.activeElement as HTMLButtonElement);
  const next =
    event.key === "Home"
      ? 0
      : event.key === "End"
        ? tabs.length - 1
        : event.key === "ArrowRight"
          ? Math.min(tabs.length - 1, current + 1)
          : Math.max(0, current - 1);
  event.preventDefault();
  tabs[next]?.focus();
}

export const LeagueSelectorRow = (
  props: typeLeagueSelectorRowProps,
): ReactNode => {
  const { row } = props;
  const scrollerRef = useRef<HTMLDivElement>(null);
  const activeKey = row.all.isActive
    ? "all"
    : (row.pinned.find((option) => option.isActive)?.leagueId ?? "");
  const chipKey = row.pinned.map((option) => option.leagueId).join(",");
  // With nothing selected (a league the catalog does not resolve) the first
  // chip keeps the row reachable by Tab.
  const hasSelection = activeKey !== "";

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller === null) return;
    const active = scroller.querySelector<HTMLElement>(
      '[aria-selected="true"]',
    );
    if (active === null) return;
    const rowBox = scroller.getBoundingClientRect();
    const chipBox = active.getBoundingClientRect();
    const next = scrollLeftToRevealChip({
      scrollLeft: scroller.scrollLeft,
      rowLeft: rowBox.left,
      rowRight: rowBox.right,
      chipLeft: chipBox.left,
      chipRight: chipBox.right,
    });
    if (next !== scroller.scrollLeft) {
      scroller.scrollLeft = next;
    }
  }, [activeKey, chipKey]);

  return (
    <nav
      aria-label={LEAGUE_CHIPS_LABEL}
      data-testid="league-row"
      className="flex items-center gap-2"
    >
      {/* The scroller bleeds to the viewport's left edge and keeps one page
          gutter of leading padding, so the first chip lines up with the page
          and a scrolled chip runs off the edge rather than into the gutter. */}
      <div
        ref={scrollerRef}
        role="tablist"
        aria-label={LEAGUE_CHIPS_LABEL}
        aria-orientation="horizontal"
        data-testid="league-row-scroller"
        className="-ms-page-gutter flex min-w-0 flex-1 items-center gap-2 overflow-x-auto ps-page-gutter [scrollbar-width:none]"
        onKeyDown={moveFocus}
      >
        <button
          type="button"
          role="tab"
          className={CHIP_BUTTON_CLASS}
          data-testid="league-row-all"
          aria-selected={row.all.isActive}
          tabIndex={row.all.isActive || !hasSelection ? 0 : -1}
          onClick={props.onSelectAll}
        >
          <span className={chipPillClassName(row.all.isActive)}>
            {row.all.label}
          </span>
        </button>
        {row.pinned.map((option) => (
          <button
            key={option.leagueId}
            type="button"
            role="tab"
            className={CHIP_BUTTON_CLASS}
            data-testid="league-row-league"
            data-league-id={option.leagueId}
            aria-selected={option.isActive}
            tabIndex={option.isActive ? 0 : -1}
            onClick={() => {
              props.onSelectLeague(option.leagueId);
            }}
          >
            <span className={chipPillClassName(option.isActive)}>
              {option.shortName}
            </span>
          </button>
        ))}
      </div>
      <button
        type="button"
        className={OUTLINE_PILL_TARGET_CLASS}
        data-testid="league-row-menu"
        aria-label={LEAGUE_MENU_OPEN_LABEL}
        onClick={props.onOpenMenu}
      >
        <span className={OUTLINE_PILL_CLASS}>
          <PlusIcon />
          {LEAGUE_MENU_MANAGE_LABEL}
        </span>
      </button>
    </nav>
  );
};
